import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Alert,
  Divider,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CreditCard,
  Security,
  CheckCircle,
  ArrowBack,
  Receipt,
  Star,
  Check
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { EasyCheckButton } from '../../common/Controls';
import SignupHelpLinks from '../Auth/SignupHelpLinks';
import { trackEvent } from '../../../utils/analytics';
import { stripeService } from '../../../services/payment/stripeService';
import { themeUtils } from '../../../theme';

// Pricing plans configuration
// Features that should be highlighted (bold) in higher plans
const HIGHLIGHT_FEATURES = ['Bulk Cheque Printing', 'Recurring PDC Printing', 'Zoho Books Integration', 'Document Attachments'];

const VAT_RATE = 0.05; // UAE VAT 5%

const PRICING_PLANS = {
  business: {
    id: 'business',
    name: 'Business',
    subtitle: '3-Year License',
    price: 750,
    currency: 'AED',
    description: '3-Year License',
    popular: false,
    features: [
      'Up to 2 users included',
      'PDC Printing',
      'Unlimited cheque printing',
      'Supports Unlimited cheque templates',
      'Advanced reporting',
      'WhatsApp & Email support',
      'Renewal only AED 300/year after Year 3'
    ]
  },
  businessPlus: {
    id: 'businessPlus',
    name: 'Business Plus',
    subtitle: '3-Year License',
    price: 1200,
    currency: 'AED',
    description: '3-Year License',
    popular: true,
    features: [
      'Up to 5 users included',
      'Bulk Cheque Printing',
      'Recurring PDC Printing',
      'Zoho Books Integration',
      'Unlimited cheque printing',
      'Supports Unlimited cheque templates',
      'Advanced reporting',
      'WhatsApp & Email support',
      'Renewal only AED 300/year after Year 3'
    ]
  },
  premium: {
    id: 'premium',
    name: 'Premium',
    subtitle: '3-Year License',
    price: 3000,
    currency: 'AED',
    description: '3-Year License',
    popular: false,
    features: [
      'Up to 10 users included',
      'Bulk Cheque Printing',
      'Recurring PDC Printing',
      'Zoho Books Integration',
      'Document Attachments',
      'Unlimited cheque printing',
      'Supports Unlimited cheque templates',
      'Advanced reporting',
      'WhatsApp & Email support',
      'Storage fee: AED 600/year from Year 2',
      'Renewal only AED 400/year after Year 3'
    ]
  }
};

const getVat = (price) => price * VAT_RATE;
const getTotal = (price) => price + getVat(price);

const PaymentContainer = styled(Box)(({ theme }) => ({
  maxWidth: '1100px',
  margin: '0 auto',
  padding: theme.spacing(3),
}));

const PaymentCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(2),
  boxShadow: `0 20px 60px rgba(37, 99, 235, 0.15)`,
  border: `1px solid ${themeUtils.colors.blue['blue-100']}`,
  marginBottom: theme.spacing(3),
}));

const PriceBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  background: themeUtils.gradients.primary,
  borderRadius: '20px',
  color: 'white',
  marginBottom: theme.spacing(3),
}));

const PlanCard = styled(Card)(({ theme, selected, popular }) => ({
  flex: 1,
  cursor: 'pointer',
  position: 'relative',
  border: selected
    ? `3px solid ${themeUtils.colors.blue['blue-600']}`
    : '2px solid transparent',
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  backgroundColor: selected ? 'rgba(37, 99, 235, 0.05)' : 'white',
  '&:hover': {
    border: `2px solid ${themeUtils.colors.blue['blue-600']}`,
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 30px ${themeUtils.colors.blue['blue-600']}25`,
  },
  ...(popular && {
    '&::before': {
      content: '"Most Popular"',
      position: 'absolute',
      top: '-12px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: themeUtils.colors.blue['blue-600'],
      color: 'white',
      padding: '4px 16px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
    }
  })
}));

const PaymentStep = ({ signupData, onBack, onPaymentSuccess, onPaymentError }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('business'); // Default to business plan
  const [paymentMethod, setPaymentMethod] = useState(''); // 'stripe' or 'code'
  const [paymentCode, setPaymentCode] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const currentPlan = PRICING_PLANS[selectedPlan];

  console.log('🟢 PaymentStep component rendered with props:', {
    signupData: signupData ? { ...signupData, password: '[HIDDEN]' } : 'NO_DATA',
    hasOnBack: !!onBack,
    hasOnPaymentSuccess: !!onPaymentSuccess,
    hasOnPaymentError: !!onPaymentError,
    showSuccessDialog: showSuccessDialog
  });

  const handleStripePayment = async () => {
    console.log('🔴 STRIPE PAYMENT CLICKED! Function started');
    console.log('🔴 Button loading state:', loading);
    console.log('🔴 SignupData received:', signupData);
    
    try {
      setLoading(true);
      setError('');

      console.log('🚀 STRIPE PAYMENT BUTTON CLICKED - Starting payment process');
      console.log('📋 Current URL:', window.location.href);
      console.log('👤 Signup data:', { ...signupData, password: '[HIDDEN]' });

      // GA4 funnel: checkout started. Also stash plan/value for the purchase event on
      // the success page (which is a separate route and has no access to this state).
      const checkoutValue = getTotal(currentPlan.price);
      trackEvent('begin_checkout', {
        plan: currentPlan.name,
        value: checkoutValue,
        currency: 'AED',
      });
      try {
        sessionStorage.setItem('signupCheckout', JSON.stringify({
          planName: currentPlan.name,
          value: checkoutValue,
          currency: currentPlan.currency,
        }));
      } catch (e) {
        // sessionStorage unavailable — purchase event will fall back to currency-only.
      }

      // Create Stripe checkout session with selected plan
      console.log('📡 About to call stripeService.createSignupPaymentSession...');
      console.log('📋 Selected plan:', selectedPlan, currentPlan);
      const result = await stripeService.createSignupPaymentSession(signupData, {
        planId: selectedPlan,
        planName: currentPlan.name,
        amount: Math.round(getTotal(currentPlan.price) * 100), // Convert to fils (smallest currency unit), includes VAT
        currency: currentPlan.currency.toLowerCase()
      });

      console.log('📥 Payment session result received:', result);

      if (result.success) {
        console.log('✅ Payment session created successfully!');
        console.log('🔗 Redirect URL:', result.sessionUrl);
        console.log('🚀 About to redirect to Stripe...');
        
        // Add a small delay to see the logs before redirect
        setTimeout(() => {
          window.location.href = result.sessionUrl;
        }, 100);
      } else {
        console.error('❌ Payment session failed with message:', result.message);
        // Ensure we're setting a string, not an object
        const errorMessage = typeof result.message === 'string' ? result.message : 
                           result.message?.Message || 
                           'Payment session creation failed';
        setError(errorMessage);
        onPaymentError?.(errorMessage);
      }
    } catch (err) {
      console.error('❌ Payment process caught error:', err);
      console.error('❌ Error stack:', err.stack);
      // Ensure we extract a proper string error message
      const errorMessage = err.message || err.toString() || 'Failed to initialize payment. Please try again.';
      setError(errorMessage);
      onPaymentError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentCodeValidation = async () => {
    console.log('🎫 PAYMENT CODE VALIDATION CLICKED!');
    console.log('🎫 Payment code:', paymentCode);
    console.log('🎫 Email:', signupData.username);
    
    if (!paymentCode.trim()) {
      setError('Please enter a payment code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      console.log('🚀 VALIDATING PAYMENT CODE...');
      
      // Call payment code validation API with plan info
      const result = await stripeService.validatePaymentCode({
        email: signupData.username,
        paymentCode: paymentCode.trim(),
        signupData,
        planId: selectedPlan,
        planName: currentPlan.name
      });

      console.log('📥 Payment code validation result:', result);

      if (result.success) {
        console.log('✅ Payment code validated successfully!');
        console.log('🎉 Showing success dialog immediately...');
        
        // Clear any errors and show success dialog immediately
        setError('');
        setSuccessMessage('');
        setShowSuccessDialog(true);
      } else {
        console.error('❌ Payment code validation failed:', result.message);
        const errorMessage = typeof result.message === 'string' ? result.message : 
                           'Invalid payment code. Please check and try again.';
        setSuccessMessage(''); // Clear any success message
        setError(errorMessage);
        onPaymentError?.(errorMessage);
      }
    } catch (err) {
      console.error('❌ Payment code validation error:', err);
      const errorMessage = err.message || 'Failed to validate payment code. Please try again.';
      setSuccessMessage(''); // Clear any success message
      setError(errorMessage);
      onPaymentError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessDialogClose = () => {
    console.log('🔄 Redirecting to login page...');
    
    // Clear signup data since account is now created
    sessionStorage.removeItem('signupData');
    
    // Redirect to login page with success message
    window.location.href = '/login?message=account_created&email=' + encodeURIComponent(signupData.username);
  };

  return (
    <PaymentContainer>
      <PaymentCard>
        <Stack spacing={2}>
          {/* Header */}
          <Box textAlign="center">
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Select your Plan and Payment Method
            </Typography>
          </Box>

          {/* Step 1: Plan Selection */}
          {!paymentMethod && (
            <Box>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 3 }} flexWrap="wrap" useFlexGap>
                {Object.values(PRICING_PLANS).map((plan) => (
                  <PlanCard
                    key={plan.id}
                    selected={selectedPlan === plan.id}
                    popular={plan.popular}
                    onClick={() => setSelectedPlan(plan.id)}
                    sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, minWidth: { md: 0 } }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box textAlign="center" mb={1}>
                        <Typography variant="h5" fontWeight={700}>
                          {plan.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {plan.subtitle}
                        </Typography>
                      </Box>

                      <Box textAlign="center" mb={1}>
                        <Typography variant="h4" fontWeight={800} color={themeUtils.colors.blue['blue-600']}>
                          {plan.price.toLocaleString()}/-
                          <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 0.5 }}>
                            {plan.currency}
                          </Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          + VAT
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 1 }} />

                      <List dense sx={{ py: 0 }}>
                        {plan.features.map((feature, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <Check sx={{ fontSize: 18, color: HIGHLIGHT_FEATURES.includes(feature) ? 'primary.main' : 'success.main' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                variant: 'body2',
                                fontWeight: HIGHLIGHT_FEATURES.includes(feature) ? 600 : 400,
                                color: HIGHLIGHT_FEATURES.includes(feature) ? 'primary.main' : 'inherit'
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>

                      {selectedPlan === plan.id && (
                        <Box textAlign="center" mt={1}>
                          <Chip
                            icon={<CheckCircle />}
                            label="Selected"
                            color="primary"
                            size="small"
                          />
                        </Box>
                      )}
                    </CardContent>
                  </PlanCard>
                ))}
              </Stack>

              {/* Pricing Notes */}
              <Box sx={{ mt: 2, px: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  * All prices are exclusive of VAT
                </Typography>
              </Box>

              {/* Payment Method Selection */}
              <Box mt={3}>
                <Typography variant="h6" fontWeight={600} gutterBottom textAlign="center">
                  Choose Payment Method
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Card
                    sx={{
                      flex: 1,
                      cursor: 'pointer',
                      border: '2px solid transparent',
                      borderRadius: '12px',
                      '&:hover': {
                        border: `2px solid ${themeUtils.colors.blue['blue-600']}`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}20`,
                      }
                    }}
                    onClick={() => {
                      setPaymentMethod('stripe');
                      stripeService.notifyLead({
                        firstName: signupData.firstName,
                        lastName: signupData.lastName,
                        companyName: signupData.companyName,
                        email: signupData.username,
                        phoneNumber: signupData.phoneNumber || '',
                        stage: 'payment_method',
                        planName: currentPlan.name,
                        paymentMethod: 'stripe',
                      });
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <CreditCard sx={{ fontSize: 36, color: themeUtils.colors.blue['blue-600'], mb: 1 }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Pay with Card
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Secure instant payment
                      </Typography>
                    </CardContent>
                  </Card>

                  <Card
                    sx={{
                      flex: 1,
                      cursor: 'pointer',
                      border: '2px solid transparent',
                      borderRadius: '12px',
                      '&:hover': {
                        border: `2px solid ${themeUtils.colors.blue['blue-600']}`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${themeUtils.colors.blue['blue-600']}20`,
                      }
                    }}
                    onClick={() => {
                      setPaymentMethod('code');
                      stripeService.notifyLead({
                        firstName: signupData.firstName,
                        lastName: signupData.lastName,
                        companyName: signupData.companyName,
                        email: signupData.username,
                        phoneNumber: signupData.phoneNumber || '',
                        stage: 'payment_method',
                        planName: currentPlan.name,
                        paymentMethod: 'code',
                      });
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <Receipt sx={{ fontSize: 36, color: themeUtils.colors.blue['blue-600'], mb: 1 }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Payment Code
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Direct billing customers
                      </Typography>
                    </CardContent>
                  </Card>
                </Stack>
              </Box>
            </Box>
          )}

          {/* Stripe Payment Flow */}
          {paymentMethod === 'stripe' && (
            <>
              {/* Selected Plan Summary */}
              <PriceBox>
                <Chip
                  label={currentPlan.subtitle}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    mb: 1
                  }}
                />
                <Typography variant="h2" fontWeight={800} sx={{ color: 'white' }}>
                  {currentPlan.currency} {getTotal(currentPlan.price).toFixed(2)}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, color: 'white', fontWeight: 400 }}>
                  {currentPlan.name} Plan - {currentPlan.description}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7, color: 'white' }}>
                  ({currentPlan.currency} {currentPlan.price} + {currentPlan.currency} {getVat(currentPlan.price).toFixed(2)} VAT)
                </Typography>
              </PriceBox>

              {/* Plan Features Summary */}
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  What's included:
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {currentPlan.features.slice(0, 4).map((feature, index) => (
                    <Chip
                      key={index}
                      icon={<Check sx={{ fontSize: 14 }} />}
                      label={feature}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '11px' }}
                    />
                  ))}
                  {currentPlan.features.length > 4 && (
                    <Chip
                      label={`+${currentPlan.features.length - 4} more`}
                      size="small"
                      color="primary"
                      sx={{ fontSize: '11px' }}
                    />
                  )}
                </Stack>
              </Box>
            </>
          )}

          {/* Payment Code Flow */}
          {paymentMethod === 'code' && (
            <Box>
              {/* Selected Plan Summary */}
              <Box sx={{ bgcolor: 'primary.main', p: 2, borderRadius: 2, mb: 3, color: 'white', textAlign: 'center' }}>
                <Chip
                  label={currentPlan.subtitle}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    mb: 1
                  }}
                  size="small"
                />
                <Typography variant="h5" fontWeight={700}>
                  {currentPlan.name} Plan
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {currentPlan.currency} {getTotal(currentPlan.price).toFixed(2)} - {currentPlan.description}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  ({currentPlan.currency} {currentPlan.price} + {currentPlan.currency} {getVat(currentPlan.price).toFixed(2)} VAT)
                </Typography>
              </Box>

              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  backgroundColor: 'rgba(37, 99, 235, 0.1)',
                  border: '1px solid rgba(37, 99, 235, 0.2)',
                  mb: 3
                }}
              >
                <Typography variant="body2">
                  <strong>Direct Billing Customers:</strong> Enter the payment code provided by EasyCheck Team. Contact support@easycheckpro.com for assistance.
                </Typography>
              </Alert>

              <TextField
                fullWidth
                label="Payment Code"
                placeholder="Enter your payment code"
                value={paymentCode}
                onChange={(e) => setPaymentCode(e.target.value)}
                error={!!error && error.includes('payment code')}
                helperText={error && error.includes('payment code') ? error : ''}
                InputProps={{
                  startAdornment: (
                    <Receipt sx={{ color: themeUtils.colors.blue['blue-600'], mr: 1 }} />
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  }
                }}
              />
            </Box>
          )}

          {/* Account Summary - Show for selected payment methods */}
          {paymentMethod && (
            <>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Order Summary
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1.5, borderRadius: 2 }}>
                  <Stack spacing={0.5}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Plan</Typography>
                      <Typography variant="caption" fontWeight={600} color="primary.main">
                        {currentPlan.name} ({currentPlan.subtitle})
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Price</Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {currentPlan.currency} {currentPlan.price.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">VAT (5%)</Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {currentPlan.currency} {getVat(currentPlan.price).toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" fontWeight={700}>Total</Typography>
                      <Typography variant="caption" fontWeight={700} color="primary.main">
                        {currentPlan.currency} {getTotal(currentPlan.price).toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Name</Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {signupData.firstName} {signupData.lastName}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Company</Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {signupData.companyName}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {signupData.username}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </>
          )}

          {/* Success Display */}
          {successMessage && (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {successMessage}
            </Alert>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Security Notice - Different for each payment method */}
          {paymentMethod === 'stripe' && (
            <Alert 
              severity="info" 
              sx={{ 
                borderRadius: 2,
                backgroundColor: 'rgba(37, 99, 235, 0.1)',
                border: '1px solid rgba(37, 99, 235, 0.2)'
              }}
            >
              <Typography variant="body2">
                <strong>Secure Payment:</strong> Your payment is processed securely by Stripe. 
                We never store your credit card information.
              </Typography>
            </Alert>
          )}

          {/* Action Buttons */}
          <Stack spacing={2}>
            {/* Payment method selection buttons */}
            {!paymentMethod && (
              <Stack direction="row" spacing={2} justifyContent="space-between">
                <EasyCheckButton
                  variant="outlined"
                  onClick={onBack}
                  startIcon={<ArrowBack />}
                  sx={{ flex: 1 }}
                >
                  Back to Account Info
                </EasyCheckButton>
              </Stack>
            )}

            {/* Stripe payment buttons */}
            {paymentMethod === 'stripe' && (
              <Stack direction="row" spacing={2} justifyContent="space-between">
                <EasyCheckButton
                  variant="outlined"
                  onClick={() => {
                    setPaymentMethod('');
                    setError('');
                    setSuccessMessage('');
                  }}
                  startIcon={<ArrowBack />}
                  size="small"
                >
                  Change Plan
                </EasyCheckButton>
                
                <EasyCheckButton
                  variant="contained"
                  onClick={handleStripePayment}
                  loading={loading}
                  startIcon={<CreditCard />}
                  sx={{
                    flex: 2,
                    background: themeUtils.gradients.primary,
                    '&:hover': {
                      background: themeUtils.gradients.primaryDark,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 35px ${themeUtils.colors.blue['blue-600']}40`,
                    },
                  }}
                >
                  {loading ? 'Redirecting to Payment...' : `Pay ${currentPlan.currency} ${getTotal(currentPlan.price).toFixed(2)} with Card`}
                </EasyCheckButton>
              </Stack>
            )}

            {/* Payment code buttons */}
            {paymentMethod === 'code' && (
              <Stack direction="row" spacing={2} justifyContent="space-between">
                <EasyCheckButton
                  variant="outlined"
                  onClick={() => {
                    setPaymentMethod('');
                    setError('');
                    setSuccessMessage('');
                    setPaymentCode(''); // Also clear the payment code
                  }}
                  startIcon={<ArrowBack />}
                  size="small"
                >
                  Change Plan
                </EasyCheckButton>
                
                <EasyCheckButton
                  variant="contained"
                  onClick={handlePaymentCodeValidation}
                  loading={loading}
                  disabled={!paymentCode.trim()}
                  startIcon={<Receipt />}
                  sx={{
                    flex: 2,
                    background: themeUtils.gradients.primary,
                    '&:hover': {
                      background: themeUtils.gradients.primaryDark,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 35px ${themeUtils.colors.blue['blue-600']}40`,
                    },
                  }}
                >
                  {loading ? 'Validating Code...' : 'Validate Payment Code'}
                </EasyCheckButton>
              </Stack>
            )}
          </Stack>

          <SignupHelpLinks stage="payment" />
        </Stack>
      </PaymentCard>

      {/* Success Dialog */}
      <Dialog 
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        maxWidth="sm"
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: 2,
            zIndex: 9999
          }
        }}
        sx={{
          zIndex: 9999
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <CheckCircle 
            sx={{ 
              fontSize: 64, 
              color: 'success.main', 
              mb: 2,
              display: 'block',
              margin: '0 auto 16px auto'
            }} 
          />
          <Typography variant="h5" fontWeight={600} color="success.main">
            Payment Code Validated Successfully!
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Stack spacing={2}>
            <Typography variant="body1" color="text.secondary">
              Your account has been created successfully. A verification email has been sent to:
            </Typography>
            <Typography variant="h6" fontWeight={600} color="primary">
              {signupData.username}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please check your email and verify your account before signing in.
            </Typography>
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <EasyCheckButton
            variant="contained"
            onClick={handleSuccessDialogClose}
            sx={{
              background: themeUtils.gradients.primary,
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '12px',
              '&:hover': {
                background: themeUtils.gradients.primaryDark,
                transform: 'translateY(-2px)',
                boxShadow: `0 12px 35px ${themeUtils.colors.blue['blue-600']}40`,
              },
            }}
          >
            OK, Go to Login
          </EasyCheckButton>
        </DialogActions>
      </Dialog>
    </PaymentContainer>
  );
};

export default PaymentStep; 